/**
 * Class to handle storing of array-based query parameters
 * Used for algolia filter urls, as the url sync from Algolia doesn't
 * work with SPA.
 */
export class QueryStringStorage {
    getQS(hashKey?: string): URLSearchParams {
        const qs = sessionStorage.getItem("qss");
        if (qs === null) {
            return new URLSearchParams("");
        } else {
            const queryObject = JSON.parse(qs);
            if (hashKey) {
                return new URLSearchParams(queryObject[hashKey]);
            }
        }
    }

    getAttributeIndex(hashKey: string, key: string): number {
        // check to see what the index for this key should be
        const qs = this.getQS(hashKey);
        const indexes = [];
        for (const k of qs.keys()) {
            const attribute = k.split("[")[0];
            if (attribute === key) {
                indexes.push(parseInt(k.split("[")[1].slice(0, -1)));
            }
        }
        return indexes.length;
    }

    clearItems(hashKey?: string): void {
        const allOfIt = JSON.parse(sessionStorage.getItem("qss"));
        if (allOfIt !== null) {
            delete allOfIt?.[hashKey];
            if (Object.keys(allOfIt).length === 0) {
                sessionStorage.removeItem("qss");
            } else {
                sessionStorage.setItem("qss", JSON.stringify(allOfIt));
            }
        }
    }

    storeItem(hashKey: string, key: string, value: string): void {
        const allOfIt =
            JSON.parse(sessionStorage.getItem("qss")) === null ? {} : JSON.parse(sessionStorage.getItem("qss"));

        const qss = this.getQS(hashKey);
        if (qss.toString() === "") {
            qss.append(`${key}[0]`, value);
        } else {
            qss.append(`${key}[${this.getAttributeIndex(hashKey, key)}]`, value);
        }
        allOfIt[hashKey] = qss.toString();
        sessionStorage.setItem("qss", JSON.stringify(allOfIt));
    }

    storeSingle(hashKey: string, key: string, value: string): void {
        const allOfIt =
            JSON.parse(sessionStorage.getItem("qss")) === null ? {} : JSON.parse(sessionStorage.getItem("qss"));

        const qss = this.getQS(hashKey);
        qss.delete(key);
        qss.append(`${key}`, value);
        allOfIt[hashKey] = qss.toString();
        sessionStorage.setItem("qss", JSON.stringify(allOfIt));
    }

    deleteItem(hashKey: string, key: string): void {
        const allOfIt = JSON.parse(sessionStorage.getItem("qss"));
        const qss = this.getQS(hashKey);
        qss.delete(key);
        if (Array.from(qss).length === 0) {
            this.clearItems(hashKey);
        } else {
            allOfIt[hashKey] = qss.toString();
            sessionStorage.setItem("qss", JSON.stringify(allOfIt));
            this.repairIndexes(hashKey);
        }
    }

    handleQueryParam(hashKey: string, key: string, value: string): void {
        // Determine if this should put or delete
        const qs = this.getQS(hashKey);
        let deleteFlag = false;
        let deleteKey: string;
        qs.forEach((v, k) => {
            if (v === value) {
                deleteFlag = true;
                deleteKey = k;
            }
        });

        if (!deleteFlag) {
            this.storeItem(hashKey, key, value);
        } else {
            this.deleteItem(hashKey, deleteKey);
        }
    }

    repairIndexes(hashKey: string): void {
        const allOfIt = JSON.parse(sessionStorage.getItem("qss"));
        const qss = this.getQS(hashKey);

        const repairObj = {};
        qss.forEach((v, k) => {
            const newKey = k.split("[")[0];
            if (`${newKey}` in repairObj) {
                repairObj[newKey].push(v);
            } else {
                repairObj[newKey] = [];
                repairObj[newKey].push(v);
            }
        });
        const fixedString = new URLSearchParams("");
        for (const [key, value] of Object.entries(repairObj)) {
            value?.forEach((v: string, index: number) => {
                fixedString.append(`${key}[${index}]`, v);
            });
        }
        allOfIt[hashKey] = fixedString.toString();
        sessionStorage.setItem("qss", JSON.stringify(allOfIt));
    }
}

export const useQueryStringStorage = (): QueryStringStorage => {
    return new QueryStringStorage();
};
